export type FilterOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'notContains'
  | 'isEmpty'
  | 'isNotEmpty'
  | 'greaterThan'
  | 'lessThan'
  | 'greaterOrEqual'
  | 'lessOrEqual'
  | 'before'
  | 'after'
  | 'isTrue'
  | 'isFalse';

export type PropertyDataType = 'string' | 'number' | 'boolean' | 'date';

export type LogicMode = 'AND' | 'OR';

export interface FilterCondition {
  id: string;
  property: string;
  operator: FilterOperator;
  value: string;
}

export interface SerializedFilterState {
  version: 1;
  conditions: FilterCondition[];
  logicOperators: LogicMode[];
  ifcTypeFilters?: string[];
}

export const OPERATORS_BY_TYPE: Record<PropertyDataType, FilterOperator[]> = {
  string: ['equals', 'notEquals', 'contains', 'notContains', 'isEmpty', 'isNotEmpty'],
  number: [
    'equals',
    'notEquals',
    'greaterThan',
    'lessThan',
    'greaterOrEqual',
    'lessOrEqual',
    'isEmpty',
    'isNotEmpty',
  ],
  boolean: ['isTrue', 'isFalse', 'isEmpty', 'isNotEmpty'],
  date: ['equals', 'notEquals', 'before', 'after', 'isEmpty', 'isNotEmpty'],
};

export const ALL_OPERATORS: FilterOperator[] = [
  ...new Set(Object.values(OPERATORS_BY_TYPE).flat()),
];

const NO_VALUE_OPERATORS: FilterOperator[] = ['isEmpty', 'isNotEmpty', 'isTrue', 'isFalse'];

export function isValueOperator(operator: FilterOperator): boolean {
  return !NO_VALUE_OPERATORS.includes(operator);
}

export function defaultOperatorForType(type: PropertyDataType): FilterOperator {
  return OPERATORS_BY_TYPE[type][0];
}

export function isActiveCondition(condition: FilterCondition): boolean {
  if (!condition.property || !condition.operator) return false;
  if (isValueOperator(condition.operator) && (condition.value ?? '').trim() === '') return false;
  return true;
}

function isTruthy(value: string): boolean {
  const lower = value.trim().toLowerCase();
  return lower === 'true' || lower === '.t.' || lower === '1';
}

function isFalsy(value: string): boolean {
  const lower = value.trim().toLowerCase();
  return lower === 'false' || lower === '.f.' || lower === '0';
}

function parseDate(value: string): number | null {
  const text = value.trim();
  let match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (match) return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  match = /^(\d{2})[./](\d{2})[./](\d{4})$/.exec(text);
  if (match) return Date.UTC(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
  return null;
}

export function detectPropertyType(values: string[]): PropertyDataType {
  const nonEmpty = values.filter((value) => value != null && String(value).trim() !== '');
  if (nonEmpty.length === 0) return 'string';

  const sample = nonEmpty.slice(0, 50);

  if (sample.every((value) => isTruthy(value) || isFalsy(value))) return 'boolean';
  if (sample.every((value) => value.trim() !== '' && !Number.isNaN(Number(value)))) return 'number';
  if (sample.every((value) => parseDate(value) != null)) return 'date';

  return 'string';
}

export function evaluateCondition(values: string[], condition: FilterCondition): boolean {
  const present = values.filter((value) => value != null && String(value).trim() !== '');
  const hasAny = present.length > 0;
  const operator = condition.operator;

  switch (operator) {
    case 'isEmpty':
      return !hasAny;
    case 'isNotEmpty':
      return hasAny;
    case 'isTrue':
      return present.some((value) => isTruthy(value));
    case 'isFalse':
      return present.some((value) => isFalsy(value));
  }

  const target = (condition.value ?? '').trim().toLowerCase();

  switch (operator) {
    case 'equals':
      return present.some((value) => value.trim().toLowerCase() === target);
    case 'notEquals':
      return !present.some((value) => value.trim().toLowerCase() === target);
    case 'contains':
      return present.some((value) => value.toLowerCase().includes(target));
    case 'notContains':
      return !present.some((value) => value.toLowerCase().includes(target));
    case 'greaterThan':
    case 'lessThan':
    case 'greaterOrEqual':
    case 'lessOrEqual': {
      const targetNumber = Number(condition.value);
      if (Number.isNaN(targetNumber)) return false;
      return present.some((value) => {
        const number = Number(value);
        if (Number.isNaN(number)) return false;
        if (operator === 'greaterThan') return number > targetNumber;
        if (operator === 'lessThan') return number < targetNumber;
        if (operator === 'greaterOrEqual') return number >= targetNumber;
        return number <= targetNumber;
      });
    }
    case 'before':
    case 'after': {
      const targetDate = parseDate(condition.value);
      if (targetDate == null) return false;
      return present.some((value) => {
        const date = parseDate(value);
        if (date == null) return false;
        return operator === 'before' ? date < targetDate : date > targetDate;
      });
    }
    default:
      return true;
  }
}

export function objectMatches(
  props: Map<string, string[]>,
  conditions: FilterCondition[],
): boolean {
  for (const condition of conditions) {
    if (!isActiveCondition(condition)) continue;
    if (!evaluateCondition(props.get(condition.property) ?? [], condition)) return false;
  }
  return true;
}

export function objectMatchesWithLogic(
  props: Map<string, string[]>,
  conditions: FilterCondition[],
  logicOperators: LogicMode[],
): boolean {
  const active: { condition: FilterCondition; index: number }[] = [];
  conditions.forEach((condition, index) => {
    if (isActiveCondition(condition)) active.push({ condition, index });
  });

  if (active.length === 0) return true;

  const groups: FilterCondition[][] = [[]];
  for (let position = 0; position < active.length; position++) {
    groups[groups.length - 1].push(active[position].condition);
    if (position < active.length - 1) {
      const operatorIndex = active[position + 1].index - 1;
      if (logicOperators[operatorIndex] === 'OR') groups.push([]);
    }
  }

  return groups.some((group) =>
    group.every((condition) => evaluateCondition(props.get(condition.property) ?? [], condition)),
  );
}
